import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { BOOKING_URL } from "../data/site";
import { track, trackLeadConversion } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REQUEST_TIMEOUT_MS = 20000;

const SIZES = ["1–10", "11–25", "26–50", "51–100", "100+"];
const INDUSTRIES = ["CPA/Accounting", "Architecture", "Engineering", "Law", "Professional Services", "Healthcare", "Other"];
const INTERESTS = ["Managed IT", "Cybersecurity", "Microsoft 365", "Backup & DR", "Cloud", "Network Support", "Co-Managed IT", "IT Consulting/vCIO", "Not sure"];
const CURRENT_IT = ["Internal team", "Current MSP", "One IT person", "Break-fix", "None", "Not sure"];
const PROMPTED = ["Poor response from current provider", "Security concerns", "Growth", "Tax season prep", "Compliance/insurance pressure", "M365 issues", "Backup concerns", "General evaluation"];
const LOOKING = ["Replace", "Supplement", "Evaluate", "Start fresh"];

const empty = {
  first_name: "", last_name: "", company: "", email: "", phone: "",
  job_title: "", company_size: "", location: "", industry: "",
  interested_in: [], current_it: "", prompted_by: "", looking_to: "", message: "",
};

export const AssessmentForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const convertedRef = useRef(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleInterest = (v) =>
    setForm((f) => ({
      ...f,
      interested_in: f.interested_in.includes(v)
        ? f.interested_in.filter((x) => x !== v)
        : [...f.interested_in, v],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.company || !form.email || !form.job_title || !form.company_size || !form.location || !form.industry || !form.message) {
      toast.error("Please complete the required fields marked with *.");
      return;
    }
    if (form.interested_in.length === 0) {
      toast.error("Please select at least one area you're interested in.");
      return;
    }
    setLoading(true);
    try {
      const source = typeof window !== "undefined" ? `Contact form (${window.location.pathname})` : "Contact form";
      const payload = { ...form, name: `${form.first_name} ${form.last_name}`.trim(), source };
      await axios.post(`${API}/leads`, payload, { timeout: REQUEST_TIMEOUT_MS });
      // Backend confirmed the lead was saved — fire conversions exactly once.
      track("generate_lead", { industry: form.industry, company_size: form.company_size });
      if (!convertedRef.current) {
        convertedRef.current = true;
        trackLeadConversion();
      }
      setDone(true);
      setForm(empty);
      toast.success("Request received — we'll be in touch within one business day.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="form-success" className="text-center py-10 px-6">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald/10 flex items-center justify-center mb-5">
          <CheckCircle2 className="h-7 w-7 text-emerald" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-charcoal mb-2">Thank you.</h3>
        <p className="text-slate-600 max-w-md mx-auto">
          Your request has been received. A Zentryx specialist will reach out within one business day.
        </p>
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
          onClick={() => track("book_intro_call_click", { location: "form-success" })}
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald hover:bg-emerald-dark text-white font-semibold transition-colors">
          Prefer to talk now? Book an Intro Call <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    );
  }

  const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-charcoal placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition";
  const selectCls = inputCls + " appearance-none";
  const label = "block text-xs uppercase tracking-widest font-semibold text-slate-400 mb-1.5";

  return (
    <form data-testid="assessment-form" onSubmit={submit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className={label}>First name *</label><input data-testid="form-first-name" className={inputCls} value={form.first_name} onChange={set("first_name")} /></div>
        <div><label className={label}>Last name *</label><input data-testid="form-last-name" className={inputCls} value={form.last_name} onChange={set("last_name")} /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className={label}>Company *</label><input data-testid="form-company" className={inputCls} value={form.company} onChange={set("company")} /></div>
        <div><label className={label}>Job title *</label><input data-testid="form-job-title" className={inputCls} value={form.job_title} onChange={set("job_title")} /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className={label}>Company email *</label><input data-testid="form-email" type="email" className={inputCls} value={form.email} onChange={set("email")} /></div>
        <div><label className={label}>Phone</label><input data-testid="form-phone" className={inputCls} value={form.phone} onChange={set("phone")} /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className={label}>Company size *</label>
          <select data-testid="form-company-size" className={selectCls} value={form.company_size} onChange={set("company_size")}>
            <option value="">Select</option>
            {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div><label className={label}>Location / city *</label><input data-testid="form-location" className={inputCls} value={form.location} onChange={set("location")} placeholder="e.g. Atlanta" /></div>
        <div>
          <label className={label}>Industry *</label>
          <select data-testid="form-industry" className={selectCls} value={form.industry} onChange={set("industry")}>
            <option value="">Select</option>
            {INDUSTRIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className={label}>Interested in * (select all that apply)</label>
        <div className="flex flex-wrap gap-2">
          {INTERESTS.map((v) => {
            const active = form.interested_in.includes(v);
            return (
              <button type="button" key={v} onClick={() => toggleInterest(v)}
                data-testid={`form-interest-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${active ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-navy"}`}>
                {v}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className={label}>Current IT setup</label>
          <select data-testid="form-current-it" className={selectCls} value={form.current_it} onChange={set("current_it")}>
            <option value="">Select</option>
            {CURRENT_IT.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>What prompted you?</label>
          <select data-testid="form-prompted-by" className={selectCls} value={form.prompted_by} onChange={set("prompted_by")}>
            <option value="">Select</option>
            {PROMPTED.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Looking to</label>
          <select data-testid="form-looking-to" className={selectCls} value={form.looking_to} onChange={set("looking_to")}>
            <option value="">Select</option>
            {LOOKING.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className={label}>Message / challenges *</label>
        <textarea data-testid="form-message" rows={4} className={inputCls + " resize-none"} value={form.message} onChange={set("message")} placeholder="Tell us about your current IT challenges" />
      </div>

      <button data-testid="form-submit-button" type="submit" disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald hover:bg-emerald-dark disabled:opacity-70 text-white font-semibold py-3.5 transition-colors">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
        {loading ? "Sending..." : "Request a Free IT Assessment"}
      </button>

      <div className="text-center">
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
          onClick={() => track("book_intro_call_click", { location: "contact-form" })}
          data-testid="form-booking-link"
          className="text-sm font-semibold text-navy hover:underline">
          Prefer to schedule directly? Book an Intro Call →
        </a>
      </div>
      <p className="text-xs text-slate-400 text-center">No obligation. We respect your privacy and never share your data.</p>
    </form>
  );
};
